/**
 * @file user-details.tsx
 * @module app/modules/sales-management/admin/user-management/user-details
 * @description Implements the user details interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.2.5
 */

import { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import blankImg from "../../../../../_admin/assets/media/avatars/blank.png";
import Email from "../../../../../_admin/assets/media/svg_uTrade/email.svg";
import PhoneCall from "../../../../../_admin/assets/media/svg_uTrade/phone-call.svg";
import { getKey, setKey } from "../../../../../Global/history";
import Loader from "../../../../../Global/loader";
import { listAdminUserDetails, listSalesRepresentative } from "../../../../../utils/storeString";
import TabsAssignedCustomers from "./tabs/tabs-assigned-customers";
import TabAssignedSalesRep from "./tabs/tab-assigned-sales-rep";
import TabsOrders from "./tabs/tabs-orders";
import APICallService from "../../../../../api/apiCallService";
import { assistAdmin } from "../../../../../api/apiEndPoints";
import { AllCustomers } from "../../../../../utils/constants";
import Method from "../../../../../utils/methods";

const UserDetails = () => {
  const { state }: { state: any } = useLocation();
  const [loading, setLoading] = useState(false);

  const [repData, setRepData] = useState<any>(null);
  const [tab, setTab] = useState<
    { name: string; content: JSX.Element | null; value: string }[]
  >([
    {
      name: "Assigned Customers",
      content: <TabsAssignedCustomers />,
      value: "1",
    },
    { name: "Orders", content: <TabsOrders />, value: "2" },
  ]);
  const [currentTab, setCurrentTab] = useState<any>(() => {
    const storedTabValue = getKey(listAdminUserDetails?.currentTab);
    const matchingTab: any = tab?.find((tab) => tab?.value == storedTabValue);
    return matchingTab || tab[0];
  });

  const createTabs = () => {
    const localTab = tab;
    let current = currentTab;
    const allTabs = localTab?.map((tab) => {
      return (
        <li className="nav-item">
          <Link
            to={"/"}
            className={
              current?.value === tab?.value
                ? "nav-link active text-active-dark"
                : "nav-link text-hover-dark"
            }
            data-bs-toggle="tab"
            onClick={() => handleSelectTab(tab)}
          >
            {tab?.name}
          </Link>
        </li>
      );
    });
    return allTabs;
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchProfileData();
      setLoading(false);
    })();
  }, [state?.id, state?.roleType]);

  useEffect(() => {
    if (state?.roleType || repData) {
      const roleType = state?.roleType || repData?.role?.[0];
      console.log("roleType", roleType);
      let firstTabName = "Assigned Customers";
      if (roleType === 1) {
        firstTabName = "Assigned Sales Rep";
      }
      if (roleType === 6) {
        firstTabName = "Assigned Activation Rep";
      }

      const newTabs = [
        {
          name: firstTabName,
          value: "1",
          content: getTabContent("1")
        },
        {
          name: "Orders",
          value: "2",
          content: getTabContent("2")
        }
      ];
      setTab(newTabs);

      setCurrentTab((prev: any) => {
        const found = newTabs.find(t => t.value === prev.value);
        return found || newTabs[0];
      });
    }
  }, [repData]);

  const fetchProfileData = async () => {
    let repId = state?.id ? state?.id : "";
    let roleType = state?.roleType;

    if (repId) {
      setKey(listAdminUserDetails.id, repId, false);
      setKey("listAdminUserDetailsRoleType", roleType);
    } else {
      repId = getKey(listAdminUserDetails.id) || "";
      roleType = Number(getKey("listAdminUserDetailsRoleType"));
    }

    let params: any = {
      "role[0][type]": roleType
    }
    let apiService = new APICallService(
      assistAdmin.info,
      params,
      { id: repId },
      "",
      "",
      "",
      AllCustomers,
      ""
    );
    let response = await apiService.callAPI();
    if (response) {
      setRepData(JSON.parse(JSON.stringify(response)));
    }
  };

  const getTabContent = (tabValue: any) => {
    const id = state?.id || getKey(listAdminUserDetails.id);
    const roleType = state?.roleType || Number(getKey("listAdminUserDetailsRoleType")) || repData?.role?.[0];

    switch (tabValue) {
      case "1":
        if (roleType === 1 || roleType === 6) {
          return <TabAssignedSalesRep representativeId={id} />;
        }
        return <TabsAssignedCustomers representativeId={id} />;
      case "2":
        return <TabsOrders representativeId={id} roleType={roleType} districts={repData?.districts} salesTarget={repData?.currentSalesTarget} />;
      default:
        return null;
    }
  };

  const currentTabContent = useMemo(
    () => getTabContent(currentTab.value),
    [currentTab?.value, repData]
  );

  const districtNames = repData?.districts
    ?.map((d: any) => d?.name)
    .filter((name: any) => name);

  const handleSelectTab = (tab: any) => {
    setCurrentTab({
      ...tab,
      content: getTabContent(tab?.value),
    });
    setKey(listAdminUserDetails?.currentTab, tab.value, false);
  };

  return (
    <>
      <div className="p-5">
        <Row className="mb-7 align-items-center">
          <Col md>
            <h1 className="fs-22 fw-bolder mb-md-0 mb-3">
              User Details
            </h1>
          </Col>
        </Row>
        {loading ? (
          <>
            <div className="d-flex justify-content-center">
              <Loader loading={loading} />
            </div>
          </>
        ) : (
          <>
            <Card className="border border-custom-color mb-7">
              <Card.Body className="pt-9 pb-0">
                <div className="d-flex flex-wrap flex-sm-nowrap mb-3">
                  <div className="me-9">
                    <div className="symbol symbol-200px symbol-fixed border border-r10px position-relative">
                      <div className="image-input d-flex flex-center rounded w-lg-200px h-lg-200px">
                        <div
                          className="image-input-wrapper shadow-none bgi-cover bgi-position-center border-r10px w-100 h-100"
                          style={{
                            backgroundImage: `url(${repData?.image ? repData?.image : blankImg
                              })`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="row align-items-center flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                      <div className="w-100 d-flex justify-content-between align-items-start flex-wrap">
                        <div className="d-flex flex-column mb-2">
                          <h3 className="text-black fs-22 fw-bolder">
                            {repData?.name} {
                              districtNames?.length > 0
                                ? `(${districtNames.length === 1
                                  ? districtNames[0]
                                  : districtNames.join(", ")
                                })`
                                : ""
                            }
                            {
                              repData?.manager?.name ? `(${repData?.manager?.name})` : ""
                            }
                          </h3>
                          <div className="d-flex flex-wrap fw-bold fs-6 mb-4 pe-2">
                            <div className="d-flex align-items-center text-black fs-16 fw-500 me-5 mb-2">
                              <span className="svg-icon svg-icon-4 me-2">
                                <img src={Email} alt="Email" />
                              </span>
                              {repData?.email}{" "}
                            </div>
                            <div className="d-flex align-items-center text-black fs-16 fw-500 mb-2">
                              <span className="svg-icon svg-icon-4 me-2">
                                <img src={PhoneCall} alt="Phone number" />
                              </span>{" "}
                              {repData?.phoneCountry} {repData?.phone}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <Col lg={12}>
                      <div className="d-flex flex-wrap flex-stack">
                        <div className="d-flex flex-column flex-grow-1 pe-8">
                          <div className="d-flex flex-wrap">
                            <div className="bg-light border rounded min-w-175px py-3 px-4 me-6 mb-3">
                              <div className="d-flex align-items-center">
                                <div className="fs-22 fw-bolder">
                                  Tsh {Method.getGeneralizedAmount(repData?.currentSalesTarget?.target ?? 0)}
                                </div>
                              </div>
                              <div className="fw-500 fs-16">Total Target</div>
                            </div>
                            <div className="bg-light border rounded min-w-175px py-3 px-4 me-6 mb-3">
                              <div className="d-flex align-items-center">
                                <div className="fs-22 fw-bolder">
                                  Tsh{" "}
                                  {Method.getGeneralizedAmount(repData?.currentSalesTarget?.achieved ?? 0)}
                                </div>
                              </div>
                              <div className="fw-500 fs-16">
                                Achieved so far
                              </div>
                            </div>
                            <div className="bg-light border rounded min-w-175px py-3 px-4 me-6 mb-3">
                              <div className="d-flex align-items-center">
                                <div className="fs-22 fw-bolder">
                                  Tsh{" "}
                                  {Method.getGeneralizedAmount(Math.max(
                                    0,
                                    (repData?.currentSalesTarget?.target ?? 0) -
                                    (repData?.currentSalesTarget?.achieved ?? 0)
                                  ))}
                                </div>
                              </div>
                              <div className="fw-500 fs-16">Remaining</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </div>
                </div>
              </Card.Body>
              <Row className="align-items-center px-lg-9 px-6">
                <Col sm>
                  <div className="d-flex h-70px">
                    <ul className="nav nav-stretch nav-line-tabs nav-custom nav-tabs nav-line-tabs nav-line-tabs-2x border-transparent fs-18 fw-600">
                      {createTabs()}
                    </ul>
                  </div>
                </Col>
              </Row>
            </Card>
            <div className="tab-content" id="myTabContent">
              <div className="tab-pane fade show active">
                <>{currentTabContent}</>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};
export default UserDetails;
